import React from 'react';
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const menuLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Blog', href: 'https://blog.languify.in/', external: true },
];


const extraLinks = [
    { label: 'Pricing', href: '/#pricing' },
    { label: 'FAQs', href: '/#faq' },
    { label: 'Privacy Policy', href: 'https://assets.languify.in/pdf/languify-privacy-policy.pdf', external: true },
    { label: 'Terms and Conditions', href: 'https://assets.languify.in/pdf/terms-%26-conditions.pdf', external: true },
];

const socialIcons = [
    { Icon: InstagramIcon, href: 'https://www.instagram.com/languify_ai/?igsh=ODY4MWd3ZzI2aWhx#', label: 'Instagram' },
    { Icon: LinkedInIcon, href: 'https://www.linkedin.com/company/languifyai/', label: 'LinkedIn' },
    { Icon: FacebookIcon, href: 'https://www.facebook.com/Languify/', label: 'Facebook' },
];

const useStyles = makeStyles(theme => ({
    footer: {
        backgroundColor: theme.palette.shades['clr-white-900'],
        padding: '48px 80px 0px',
        [theme.breakpoints.down('md')]: {
            padding: '32px 24px 0px',
        },
    },
    topRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '40px',
        paddingBottom: '40px',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            gap: '32px',
        },
    },
    logoCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: '180px',
    },
    logoBox: {
        display: 'inline-flex',
        alignItems: 'center',
        border: `1.5px solid ${theme.palette.neutral['clr-200']}`,
        borderRadius: '6px',
        padding: '6px 12px',
        gap: '6px',
        width: 'fit-content',
    },
    logoText: {
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 700,
        fontSize: '0.9rem',
        lineHeight: 1,
    },
    byLanguify: {
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.6rem',
        color: theme.palette.neutral['clr-400'],
        fontWeight: 400,
        alignSelf: 'flex-end',
        marginBottom: '1px',
    },
    socialRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
    },
    socialIcon: {
        color: '#EE634E',
        fontSize: '1.4rem',
        cursor: 'pointer',
        transition: 'opacity 0.2s',
        '&:hover': {
            opacity: 0.75,
        },
    },
    col: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '140px',
    },
    colHeading: {
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        color: theme.palette.neutral['clr-900'],
        marginBottom: '12px',
    },
    colLink: {
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.875rem',
        color: theme.palette.neutral['clr-600'],
        textDecoration: 'none',
        lineHeight: '2',
        transition: 'color 0.2s',
        '&:hover': {
            color: '#0284C5',
        },
    },
    bottomBar: {
        borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
        padding: '16px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            gap: '8px',
            textAlign: 'center',
        },
    },
    bottomText: {
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
        color: theme.palette.neutral['clr-400'],
    },
}));

const FooterLink = ({ href, external, children, className }) => (
    <a href={href} className={className} rel="noreferrer" {...(external ? { target: '_blank' } : {})}>
        {children}
    </a>
);

function Footer() {
    const classes = useStyles();

    return (
        <Box className={classes.footer}>
            <Box className={classes.topRow}>

                {/* Column 1 — Logo + Socials */}
                <Box className={classes.logoCol}>
                    <Box className={classes.logoBox}>
                        <Typography component="span" className={classes.logoText} sx={{ color: '#0284C5' }}>
                            IELTS
                        </Typography>
                        <Typography component="span" className={classes.logoText} sx={{ color: 'neutral.clr-300' }}>
                            |
                        </Typography>
                        <Typography component="span" className={classes.logoText} sx={{ color: 'neutral.clr-900' }}>
                            Band
                        </Typography>
                        <Typography component="span" className={classes.logoText} sx={{ color: 'neutral.clr-900' }}>
                            Boost
                        </Typography>
                        <Typography component="span" className={classes.byLanguify}>
                            by languify
                        </Typography>
                    </Box>

                    {/* Social Icons — replace with actual links when ready */}
                    <Box className={classes.socialRow}>
                        {socialIcons.map(({ Icon, href, label }) => (
                            <a key={label} href={href} rel="noreferrer" aria-label={label}>
                                <Icon className={classes.socialIcon} />
                            </a>
                        ))}
                    </Box>
                </Box>

                {/* Column 2 — Menu */}
                <Box className={classes.col}>
                    <Typography className={classes.colHeading}>Menu</Typography>
                    {menuLinks.map((item) => (
                        <FooterLink key={item.label} href={item.href} external={item.external} className={classes.colLink}>
                            {item.label}
                        </FooterLink>
                    ))}
                </Box>

{/* Column 4 — Extra Links */}
                <Box className={classes.col}>
                    <Typography className={classes.colHeading}>Extra Links</Typography>
                    {extraLinks.map((item) => (
                        <FooterLink key={item.label} href={item.href} className={classes.colLink}>
                            {item.label}
                        </FooterLink>
                    ))}
                </Box>

            </Box>

            {/* Bottom Bar */}
            <Box className={classes.bottomBar}>
                <Typography className={classes.bottomText}>
                    © 2026 IELTS BandBoost by Languify
                </Typography>
                <Typography className={classes.bottomText}>
                    Built with Languify
                </Typography>
            </Box>
        </Box>
    );
}

export default Footer;
